import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, Modal, Select } from 'antd';
import apiService from '../../api/apiService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function RoomOrderModel({ isOpen, onClose, onSave, existingData, hotelId }) {
  const [form] = Form.useForm();
  const [error, setError] = useState('');
  const [inventoryOptions, setInventoryOptions] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [bookingsOptions, setBookingsOptions] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [maxQty, setMaxQty] = useState(0);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [bookingRooms, setBookingRooms] = useState([]);

  useEffect(() => {
    fetchInventory();
    fetchBookings();
    fetchGuests();
    fetchRooms();
  }, []);

  const handleBookingChange = bookingId => {
    setSelectedBookingId(bookingId);
    const selectedBooking = allBookings.find(
      booking => booking.id === bookingId
    );
    if (selectedBooking && selectedBooking.room) {
      setBookingRooms(
        selectedBooking.room.map(room => ({
          value: room.id,
          roomNumber: room.room_number,
        }))
      );
    } else {
      setBookingRooms([]);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await apiService.get(`/inventory/?hotel=${hotelId}`);
      setInventoryOptions(
        response.map(item => ({
          value: item.id,
          label: item.item_name,
          quantity: item.quantity,
        }))
      );
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const uniqueBookingsMap = new Map();

  const fetchBookings = async () => {
    try {
      const response = await apiService.get(`/bookings/?hotel=${hotelId}`);
      const filtered = response.filter(b => b.status === 'check_in');
      setAllBookings(filtered);

      filtered.forEach(item => {
        if (!uniqueBookingsMap.has(item.id)) {
          uniqueBookingsMap.set(item.id, {
            value: item.id,
            guest: item.guest,
          });
        }
      });

      setBookingsOptions(Array.from(uniqueBookingsMap.values()));
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await apiService.get(`/rooms/?hotel=${hotelId}`);
      setRooms(
        response.map(item => ({
          value: item.id,
          roomNumber: item.room_number,
        }))
      );
    } catch (error) {
      console.error('Error fetching Rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await apiService.get(`/guests/?hotel=${hotelId}`);
      setGuests(response);
    } catch (error) {
      console.log(error);
    }
  };

  const getGuestName = guestId => {
    const guest = guests.find(g => g.id === guestId);
    return guest?.name || '-';
  };

  useEffect(() => {
    if (existingData && allBookings.length > 0 && inventoryOptions.length > 0) {
      form.setFieldsValue({
        order_details: existingData.order_details || '',
        amount: existingData.amount || 0,
        quantity: existingData.quantity || 0,
        booking: existingData.booking || '',
        inventory: existingData.inventory || '',
        room: existingData.room || null,
      });

      handleBookingChange(existingData.booking); // ✅ Set room list

      const qty = getItemQty(existingData.inventory);
      setSelectedItem(existingData.inventory);
      setMaxQty(qty);
    }
  }, [existingData, allBookings, inventoryOptions, form]);

  const handleInventoryChange = value => {
    setSelectedItem(value);
    const qty = getItemQty(value);
    setMaxQty(qty);
  };

  const getItemQty = itemId => {
    const item = inventoryOptions.find(i => i.value === itemId);
    return item ? item.quantity : 0;
  };

  const handleSaveAndEdit = async values => {
    const payload = {
      order_details: values.order_details,
      amount: values.amount,
      quantity: values.quantity,
      booking: values.booking,
      inventory: values.inventory,
      hotel: localStorage.getItem('hotel'),
      room: values.room,
    };

    try {
      let response;
      if (existingData) {
        response = await apiService.put(
          `/room-orders/${existingData.id}/?hotel=${hotelId}`,
          payload
        );
        toast.success('Room order updated successfully!');
      } else {
        response = await apiService.post(
          `/room-orders/?hotel=${hotelId}`,
          payload
        );

        toast.success('Room order created successfully!');
      }
      onSave(response);
      handleCancel();
    } catch (error) {
      toast.error('Failed to save room order! Please try again!');
      console.error(error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError('');
    setSelectedItem(null);
    setMaxQty(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      centered
      title={existingData ? 'Edit Order' : 'Add Order'}
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={handleSaveAndEdit}>
        <Form.Item
          label="Booking/Guest"
          name="booking"
          rules={[{ required: true, message: 'Please select a Booking/Guest' }]}
        >
          <Select
            showSearch
            placeholder={loading ? 'Loading...' : 'Select Booking/Guest'}
            disabled={loading}
            loading={loading}
            onChange={handleBookingChange}
            optionFilterProp="children"
            style={{ marginBottom: '8px' }}
          >
            {bookingsOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {getGuestName(option.guest)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Room"
          name="room"
          rules={[{ required: true, message: 'Please select a room' }]}
        >
          <Select
            placeholder="Select Room"
            /* disabled={bookingRooms.length === 0} */
            style={{ marginBottom: '8px' }}
          >
            {bookingRooms.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.roomNumber}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Inventory Item"
          name="inventory"
          rules={[
            { required: true, message: 'Please select an inventory item' },
          ]}
        >
          <Select
            showSearch
            placeholder={loading ? 'Loading...' : 'Select inventory item'}
            disabled={loading}
            loading={loading}
            onChange={handleInventoryChange}
            optionFilterProp="children"
            style={{ marginBottom: '8px' }}
          >
            {inventoryOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Quantity"
          name="quantity"
          rules={[
            { required: true, message: 'Please enter the quantity' },
            () => ({
              validator(_, value) {
                if (!value || value <= maxQty) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(`Only ${maxQty} available in inventory`)
                );
              },
            }),
          ]}
        >
          <InputNumber min={1} max={maxQty} placeholder={`Max: ${maxQty}`} />
        </Form.Item>

        {selectedItem && (
          <div style={{ marginBottom: 16, color: '#888' }}>
            <strong>Max Available:</strong> {maxQty}
          </div>
        )}

        <Form.Item
          label="Amount"
          name="amount"
          rules={[
            { required: true, message: 'Please enter the amount per item' },
          ]}
        >
          <InputNumber
            min={0}
            placeholder="Enter amount per item"
            controls={false}
          />
        </Form.Item>

        <Form.Item
          label="Order Details"
          name="order_details"
          rules={[{ required: true, message: 'Please enter order details' }]}
          style={{ marginBottom: '8px' }}
        >
          <Input placeholder="Enter order details" />
        </Form.Item>

        <div className="form-actions d-flex justify-content-end">
          <Button onClick={handleCancel} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            Save
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

export default RoomOrderModel;
